import React, { useEffect, useState } from "react";
import { Button, Card, CardHeader, PageHeader, Select, Tabs, TextInput } from "@opendate-app/setlist";

import { useMarketingSettingsContext } from "./MarketingSettingsIndexContainer";
import VenueOwnershipForm from "./VenueOwnerships/VenueOwnershipForm";
import SendDomainsContainer from "./SendDomains/SendDomainsContainer";
import TwilioAccountContainer from "./SendDomains/TwilioAccountContainer";
import EmailHealthCard from "./EmailHealth/EmailHealthCard";
import AttributionPartnersApp from "../../AttributionPartners/AttributionPartnersApp";

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
    color: "var(--sl-text-primary)",
  },
  panels: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
    minWidth: 0,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  field: {
    maxWidth: 480,
  },
  sortGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  sortLabel: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 8,
  },
  sortControls: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
  },
  headerActions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
  },
};

const SORT_COLUMN_OPTIONS = [
  { value: "start_time", label: "Event Start Time" },
  { value: "announce_time", label: "Announce Time" },
];

const SORT_ORDER_OPTIONS = [
  { value: "asc", label: "Ascending (Earliest First)" },
  { value: "desc", label: "Descending (Latest First)" },
];

const SORT_GROUPS = [
  { label: "Upcoming Events", prefix: "upcoming_events", column: "start_time", order: "asc" },
  { label: "Just Announced Events", prefix: "just_announced_events", column: "announce_time", order: "desc" },
];

const TABS = [
  { value: "general", label: "General" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "branding", label: "Branding" },
];

const ATTRIBUTION_TAB = { value: "attribution", label: "Attribution" };

const initialTab = (tabs, search) => {
  const tab = new URLSearchParams(search).get("tab");
  return tabs.some((item) => item.value === tab) ? tab : "general";
};

const GeneralSettings = () => {
  const { availableTimezones, formState, setFormState } = useMarketingSettingsContext();

  const setField = (key, value) => setFormState((prev) => ({ ...prev, [key]: value }));

  return (
    <>
      <Card header={<CardHeader title="Marketing alert emails" supportingText="Who receives marketing alerts for this venue." />}>
        <div style={styles.field}>
          <TextInput
            label="Email addresses"
            hint="Separate addresses with commas."
            type="text"
            value={formState.marketing_alert_emails || ""}
            onChange={(e) => setField("marketing_alert_emails", e.target.value)}
            placeholder="email1@example.com, email2@example.com"
          />
        </div>
      </Card>
      <Card header={<CardHeader title="Timezone" supportingText="The venue's local timezone." />}>
        <div style={styles.field}>
          <Select
            aria-label="Timezone"
            searchable
            options={(availableTimezones || []).map((timezone) => ({ value: timezone, label: timezone }))}
            value={formState.time_zone || null}
            onChange={(value) => setField("time_zone", value)}
          />
        </div>
      </Card>
      <Card header={<CardHeader title="Marketing widget event sorting" supportingText="How events are ordered in your marketing widgets." />}>
        <div style={styles.sortGroup}>
          {SORT_GROUPS.map((group) => (
            <div key={group.prefix}>
              <div style={styles.sortLabel}>{group.label}</div>
              <div style={styles.sortControls}>
                <Select
                  aria-label={`${group.label} sort by`}
                  width={240}
                  options={SORT_COLUMN_OPTIONS}
                  value={formState[`${group.prefix}_sort_column`] || group.column}
                  onChange={(value) => setField(`${group.prefix}_sort_column`, value)}
                />
                <Select
                  aria-label={`${group.label} sort order`}
                  width={280}
                  options={SORT_ORDER_OPTIONS}
                  value={formState[`${group.prefix}_sort_order`] || group.order}
                  onChange={(value) => setField(`${group.prefix}_sort_order`, value)}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
};

const EmailSettings = () => {
  const { team, helpCenterEnabled, emailHealthVisible } = useMarketingSettingsContext();

  return (
    <>
      {emailHealthVisible ? <EmailHealthCard teamId={team.id} helpCenterEnabled={helpCenterEnabled} /> : null}
      <SendDomainsContainer />
    </>
  );
};

const AttributionSettings = () => {
  const { attribution } = useMarketingSettingsContext();

  return <AttributionPartnersApp {...attribution} />;
};

const TAB_CONTENT = {
  general: GeneralSettings,
  email: EmailSettings,
  sms: TwilioAccountContainer,
  branding: VenueOwnershipForm,
  attribution: AttributionSettings,
};

const SELF_SAVING_TABS = ["sms", "attribution"];

const MarketingSettingsIndex = () => {
  const {
    venueOwnerships,
    setVenueOwnership,
    venueOwnership,
    updateMarketingSettings,
    attribution,
  } = useMarketingSettingsContext();

  const tabs = attribution ? [...TABS, ATTRIBUTION_TAB] : TABS;
  const [tab, setTab] = useState(() => initialTab(tabs, window.location.search));
  const teamWide = tab === "attribution";

  const setQueryParam = (key, value) => {
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    window.history.replaceState(window.history.state, "", url);
  };

  const changeTab = (value) => {
    setTab(value);
    setQueryParam("tab", value);
  };

  useEffect(() => {
    if (new URLSearchParams(window.location.search).has("venue_ownership_id")) {
      setQueryParam("venue_ownership_id", venueOwnership.id);
    }
  }, []);

  const changeVenue = (id) => {
    setVenueOwnership(venueOwnerships.find((ownership) => ownership.id === id));
    setQueryParam("venue_ownership_id", id);
  };

  return (
    <div style={styles.page}>
      <PageHeader
        title="Marketing settings"
        supportingText={teamWide ? "Settings for your whole team." : "Settings for the selected venue."}
        actions={(
          <div style={styles.headerActions}>
            {!teamWide ? (
              <Select
                aria-label="Venue"
                width={240}
                searchable={venueOwnerships.length > 8}
                options={venueOwnerships.map((ownership) => ({ value: ownership.id, label: ownership.venueable.name }))}
                value={venueOwnership.id}
                onChange={changeVenue}
              />
            ) : null}
            {!SELF_SAVING_TABS.includes(tab) ? <Button variant="primary" onClick={updateMarketingSettings}>Save</Button> : null}
          </div>
        )}
      />
      <div className="marketing-settings-body">
        <Tabs id="marketing-settings" items={tabs} value={tab} onChange={changeTab} orientation="vertical" collapseBelow={0} track={false} aria-label="Marketing settings sections" />
        <div style={styles.panels}>
          {tabs.map(({ value, label }) => {
            const TabContent = TAB_CONTENT[value];
            const active = value === tab;

            return (
              <div
                key={value}
                id={`marketing-settings-panel-${value}`}
                role="tabpanel"
                aria-labelledby={`marketing-settings-tab-${value}`}
                aria-label={label}
                hidden={!active}
                style={active ? styles.section : undefined}
              >
                <TabContent />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MarketingSettingsIndex;
